import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import AccountLoadingScreen from '@/karehero-app/access/AccountLoadingScreen';
import FundingScreen from '@/karehero-app/access/FundingScreen';
import WorkEmailScreen from '@/karehero-app/access/WorkEmailScreen';
import AccessVerifiedScreen from '@/karehero-app/onboarding/current-flow/AccessVerifiedScreen';
import Onboarding from '@/karehero-app/onboarding/Onboarding';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

const welcome = (
  <WelcomeScreen
    onCreateAccount={noop}
    onSignIn={noop}
    onForgotPassword={noop}
  />
);

// Onboarding minis jump straight to a step. Step order:
// fork(0) → 1 about(1) → 2 conditions(2) → 3 arrangement(3) → 4 postcode(4)
// → 5 hours(5) → 6 help(6) → 7 circle(7).
const ob = (
  step: number,
  extra?: Partial<Parameters<typeof Onboarding>[0]>,
) => (
  <Onboarding
    onComplete={noop}
    account={{ onSignOut: noop }}
    initialStep={step}
    {...extra}
  />
);

// The end-to-end sign-up and onboarding handover, Kinde steps included. The
// Kinde pages are hosted by Kinde in production — they're laid out here so
// the journey is designed end to end, not forgotten at the auth boundary.
export const onboardingSpec: HandoverSpec = {
  id: 'onboarding',
  title: 'Sign-up & onboarding',
  description:
    'The full journey from the welcome screen to a set-up care circle: Kinde hosted auth (Google or email, one-time code, set password), a loading interstitial while the account is created, access verification, then seven questions that build the care profile and invite the care circle.',
  links: [
    { label: 'Live flow (sign up)', url: '/karehero-app/signup' },
    {
      label: 'Production replica (for comparison)',
      url: '/karehero-app/onboarding-current',
    },
  ],
  flows: [
    {
      title: 'Sign up to home',
      steps: [
        {
          id: 'welcome',
          title: 'Welcome',
          tap: 'Open the app',
          render: () => welcome,
        },
        {
          id: 'kinde-register',
          title: 'Create account (Kinde)',
          tap: 'Tap ‘Create account’',
          render: () => (
            <>
              {welcome}
              <KindeHostedSheet variant='register' onClose={noop} />
            </>
          ),
        },
        {
          id: 'kinde-code',
          title: 'Check your inbox (Kinde)',
          tap: 'Tap ‘Create your account’',
          render: () => (
            <>
              {welcome}
              <KindeHostedSheet
                variant='register'
                initialStep='code'
                onClose={noop}
              />
            </>
          ),
        },
        {
          id: 'kinde-password',
          title: 'Set your password (Kinde)',
          tap: 'Enter the emailed code',
          render: () => (
            <>
              {welcome}
              <KindeHostedSheet
                variant='register'
                initialStep='password'
                onClose={noop}
              />
            </>
          ),
        },
        {
          id: 'loading',
          title: 'Setting up your account',
          tap: 'Tap ‘Continue’',
          render: () => <AccountLoadingScreen onContinue={noop} />,
        },
        {
          id: 'funding',
          title: 'Welcome & funding',
          tap: 'Loader completes',
          render: () => <FundingScreen onContinue={noop} />,
          note: 'The app bar starts here and stays for the rest of the journey, the intake included. The account exists from this screen on, and the avatar is the only route to Help and Sign out until Home. It carries a person icon rather than initials until the intake asks their name, and the logo goes nowhere because there is nothing behind it yet.',
        },
        {
          id: 'verify',
          title: 'Verify access',
          tap: '‘Through work’',
          render: () => (
            <WorkEmailScreen
              account={{ onSignOut: noop }}
              onSubmit={noop}
              onBack={noop}
            />
          ),
        },
        {
          id: 'verified',
          title: 'You’re in',
          tap: 'Code checks out',
          render: () => <AccessVerifiedScreen onContinue={noop} />,
        },
        {
          id: 'fork',
          title: 'Are you supporting someone?',
          tap: 'Tap ‘Continue’',
          render: () => ob(0),
        },
        {
          id: 'about',
          title: 'Step 1 · who you care for',
          tap: 'Pick an option, Next',
          render: () => ob(1),
        },
        {
          id: 'conditions',
          title: 'Step 2 · conditions',
          tap: 'Next',
          render: () => ob(2, { initialAnswers: { '2': 'yes' } }),
        },
        {
          id: 'arrangement',
          title: 'Step 3 · care arrangement',
          tap: 'Next',
          render: () => ob(3),
        },
        {
          id: 'postcode',
          title: 'Step 4 · postcode',
          tap: 'Next',
          render: () => ob(4),
        },
        {
          id: 'hours',
          title: 'Step 5 · hours of care',
          tap: 'Next',
          render: () => ob(5),
        },
        {
          id: 'help',
          title: 'Step 6 · what you need',
          tap: 'Next',
          render: () => ob(6),
        },
        {
          id: 'circle',
          title: 'Step 7 · care circle',
          tap: 'Next',
          render: () => ob(7),
        },
      ],
    },
  ],
  states: [
    {
      id: 'stuck-before-magic-link',
      title: 'Stuck before the magic link',
      kind: 'edge',
      repro:
        'Signed up in the app before opening the magic link: wrong account, no way forward. The avatar opens Settings, which names the account and offers Help and Sign out',
      render: () => (
        <FundingScreen onContinue={noop} onSignOut={noop} initialSheetOpen />
      ),
    },
    {
      id: 'sign-in',
      title: 'Sign in instead',
      kind: 'edge',
      repro: '‘Sign in’ on the welcome screen. Same sheet, no password step',
      render: () => (
        <>
          {welcome}
          <KindeHostedSheet variant='sign-in' onClose={noop} />
        </>
      ),
    },
    {
      id: 'permissions',
      title: 'Member permissions sheet',
      kind: 'happy',
      repro:
        'Tap a member on the care circle step. Everyone sees everything; only people close to the care can keep their own actions',
      render: () => ob(7, { initialOpenMember: 0 }),
    },
    {
      id: 'invite',
      title: 'Invite someone new',
      kind: 'happy',
      repro: '‘Add another’ on the care circle step. Role picks the defaults',
      render: () => ob(7, { initialInviteOpen: true }),
    },
    {
      id: 'conditions-quick-add',
      title: 'Quick add a condition',
      kind: 'happy',
      repro:
        '‘+ Add’ opens a non-modal sheet. Typed chips join the row selected',
      render: () => ob(2, { initialAnswers: { '2': 'yes' } }),
    },
    {
      id: 'no-conditions',
      title: 'No conditions',
      kind: 'empty',
      repro: '‘No’ on step 2. The quick add row never appears',
      render: () => ob(2, { initialAnswers: { '2': 'no' } }),
    },
    {
      id: 'self-mode',
      title: 'Self-care phrasing',
      kind: 'edge',
      repro: '‘I need care for myself’ at the fork. Same steps, you-phrasing',
      render: () => ob(1, { initialMode: 'self' }),
    },
    {
      id: 'exploring',
      title: 'Just exploring',
      kind: 'edge',
      repro: '‘Not yet, just exploring’ skips every question on Next',
      render: () => ob(0),
    },
  ],
  requirements: [
    {
      text: 'Kinde hosted auth: Google SSO or email, one-time code, set password on registration',
      status: 'partial',
      where: 'Create account → set password',
      note: 'The sheet is a view-only replica of the staging hosted pages. The real screens are Kinde-hosted and styled via Kinde branding config',
    },
    {
      text: 'Loading interstitial after set password, using the logo mark',
      status: 'built',
      where: 'Setting up your account',
      note: 'Animated SVG at public/vector/karehero-loader.svg, respects reduced motion',
    },
    {
      text: 'Funding triage: work email, partner code or payroll ID, each with its own verify step',
      status: 'built',
      where: 'Welcome & funding → verify access',
    },
    {
      text: '‘You’re in’ interstitial once access checks out',
      status: 'built',
      where: 'You’re in',
    },
    {
      text: 'Triage fork: carer, self-carer or exploring. Explorers skip onboarding',
      status: 'built',
      where: 'Are you supporting someone?',
    },
    {
      text: 'Seven questions: who, conditions, arrangement, postcode, hours, needs, circle',
      status: 'built',
      where: 'Steps 1 to 7',
      note: 'Self-carers get the same steps with you-phrasing',
    },
    {
      text: 'Conditions quick add with custom entries via a non-modal sheet',
      status: 'built',
      where: 'Step 2',
    },
    {
      text: 'Care circle invites with all-or-nothing view (everyone sees everything); people close to the care can also create and edit their own actions, defaulted by relationship',
      status: 'built',
      where: 'Step 7',
    },
    {
      text: 'Answers persist to the care profile and surface in the CAM control panel',
      status: 'not-built',
      note: 'Prototype is front-end only. The control panel overview shows the target shape',
    },
    {
      text: 'Invite emails and notifications to invited members',
      status: 'out-of-scope',
      note: 'Needs the notification workstream',
    },
    {
      text: 'Voice input (‘Tell me instead’) on every step',
      status: 'out-of-scope',
      note: 'Parked. Hidden behind SHOW_TELL_ME_INSTEAD in Onboarding.tsx',
    },
  ],
  components: {
    mui: [
      'TextField',
      'Chip',
      'Card',
      'Radio',
      'Checkbox',
      'Switch',
      'Avatar',
      'Typography',
    ],
    existing: [
      'Button (llama)',
      'PageHeader (llama)',
      'BottomSheet',
      'StatusBar',
      'HomeIndicator',
      'PhoneFrame',
    ],
    new: [
      'KindeHostedSheet',
      'AccountLoadingScreen',
      'FundingScreen',
      'StepScreen',
      'WorkEmailScreen',
      'OptionCard',
      'YesNo',
      'QuickAddChips',
      'ProgressBar',
    ],
  },
  componentsNote:
    'Onboarding internals live in karehero-app/Onboarding.tsx, the access flow in karehero-app/access-flow/. The Kinde steps are Kinde-hosted in production: the sheet replicates them for flow design only, and the real pages are themed through Kinde’s branding config.',
};
